import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ClipboardCheck,
  Eye,
  FileText,
  Search,
  Users,
} from 'lucide-react';
import { FirestoreService } from '../services/firestore';
import { useAuthStore } from '../stores/authStore';
import SubmissionReviewModal from '../components/assessment/SubmissionReviewModal';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'submitted', label: 'Pending' },
  { key: 'in-review', label: 'Drafts' },
  { key: 'graded', label: 'Graded' },
];

const getDisplayStatus = (submission = {}) => {
  const status = String(submission.gradingStatus || '').trim().toLowerCase();
  if (!status || status === 'pending-submission') return 'submitted';
  return status;
};

const formatSubmittedAt = (value) => {
  if (!value) return 'Not submitted';
  if (value?.seconds) return new Date(value.seconds * 1000).toLocaleString();
  return new Date(value).toLocaleString();
};

const isSubmittedAttempt = (submission = {}) => {
  const status = String(submission.status || '').trim().toLowerCase();
  return status === 'submitted' || status === 'timed-out';
};

const GradingInboxPage = () => {
  const { user, userData } = useAuthStore();
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;
    const loadInbox = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const results = await FirestoreService.getGradingInboxSubmissions(user.uid);
        setSubmissions(results);
      } catch (error) {
        console.error('Failed to load grading inbox:', error);
        setLoadError('Could not load grading inbox.');
      } finally {
        setIsLoading(false);
      }
    };
    loadInbox();
  }, [user?.uid]);

  const stats = useMemo(() => {
    const total = submissions.length;
    const pending = submissions.filter((item) => getDisplayStatus(item) === 'submitted').length;
    const drafts = submissions.filter((item) => getDisplayStatus(item) === 'in-review').length;
    const graded = submissions.filter((item) => getDisplayStatus(item) === 'graded').length;
    return { total, pending, drafts, graded };
  }, [submissions]);

  const filteredSubmissions = useMemo(() => {
    let next = submissions.filter(isSubmittedAttempt);
    if (activeFilter !== 'all') {
      next = next.filter((item) => getDisplayStatus(item) === activeFilter);
    }

    const query = searchTerm.trim().toLowerCase();
    if (query) {
      next = next.filter((item) =>
        String(item.learnerName || '').toLowerCase().includes(query) ||
        String(item.learnerEmail || '').toLowerCase().includes(query) ||
        String(item.documentTitle || item.documentName || '').toLowerCase().includes(query) ||
        String(item.submittedToTeacherName || '').toLowerCase().includes(query) ||
        String(item.submittedToTutorName || '').toLowerCase().includes(query)
      );
    }

    return next;
  }, [activeFilter, searchTerm, submissions]);

  const openSubmission = async (submission) => {
    setSelectedSubmission(submission);
    setIsLoadingDocument(true);
    try {
      const doc = await FirestoreService.getDocument(submission.documentId);
      setSelectedDocument(doc);
    } catch (error) {
      console.error('Failed to load submission document:', error);
      setSelectedDocument(null);
    } finally {
      setIsLoadingDocument(false);
    }
  };

  const handleSaveGrade = async (gradeData) => {
    if (!selectedSubmission?.id) return;
    await FirestoreService.saveSubmissionGrade(selectedSubmission.id, gradeData);
    const nextSubmission = { ...selectedSubmission, ...gradeData };
    setSelectedSubmission(nextSubmission);
    setSubmissions((current) =>
      current.map((entry) => (entry.id === selectedSubmission.id ? nextSubmission : entry))
    );
  };

  const currentRoleLabel = userData?.userRole || userData?.role || 'Reviewer';

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <div className="relative mb-8 overflow-hidden rounded-xl bg-gradient-to-r from-sigma-600 to-sigma-800 p-6">
        <div className="relative z-10 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="rounded-md p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
                <ClipboardCheck className="h-6 w-6" />
                Grading Inbox
              </h1>
              <p className="mt-0.5 text-sm text-sigma-100">
                Review formal and class-practice submissions routed to you as {currentRoleLabel}.
              </p>
            </div>
          </div>
        </div>
        <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-white/5" />
        <div className="absolute -bottom-8 -left-8 h-40 w-40 rounded-full bg-white/5" />
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs font-medium text-gray-500">Total</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs font-medium text-amber-700">Pending</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">{stats.pending}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs font-medium text-blue-700">Drafts</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">{stats.drafts}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs font-medium text-emerald-700">Graded</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">{stats.graded}</div>
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1">
          {FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              onClick={() => setActiveFilter(filter.key)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                activeFilter === filter.key
                  ? 'bg-white text-sigma-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search learner or document"
            className="w-full rounded-lg border border-gray-200 pl-9 pr-3 py-2 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-sigma-600" />
        </div>
      ) : loadError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {loadError}
        </div>
      ) : filteredSubmissions.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white py-16 text-center">
          <FileText className="mx-auto mb-4 h-12 w-12 text-gray-300" />
          <h2 className="text-lg font-semibold text-gray-900">No submissions in this view</h2>
          <p className="mt-1 text-sm text-gray-500">
            {submissions.length === 0
              ? 'Nothing has been routed to your grading inbox yet.'
              : 'Try clearing filters or searching with a different learner or document name.'}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Learner</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Document</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Route</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Submitted</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Score</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredSubmissions.map((submission) => {
                  const status = getDisplayStatus(submission);
                  return (
                    <tr key={submission.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 align-top">
                        <div className="font-medium text-gray-900">{submission.learnerName || 'Unknown learner'}</div>
                        <div className="text-sm text-gray-500">{submission.learnerEmail || 'No email'}</div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="font-medium text-gray-900">{submission.documentTitle || submission.documentName || submission.documentId}</div>
                        <div className="text-sm text-gray-500">
                          {submission.assessmentCategory === 'formal'
                            ? 'Formal'
                            : submission.assessmentType === 'class-practice'
                              ? 'Informal • Class Practice'
                              : 'Assessment'}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-gray-600">
                        <div>{submission.submittedToTeacherName || 'No teacher set'}</div>
                        {submission.submittedToTutorName && (
                          <div className="text-xs text-gray-500">Tutor: {submission.submittedToTutorName}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-gray-600">
                        {formatSubmittedAt(submission.submittedAt)}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          status === 'graded'
                            ? 'bg-emerald-100 text-emerald-700'
                            : status === 'in-review'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-amber-100 text-amber-700'
                        }`}>
                          {status === 'in-review' ? 'Draft' : status === 'graded' ? 'Graded' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-gray-700">
                        {submission.score != null && submission.percentage != null ? `${submission.score} (${submission.percentage}%)` : '-'}
                      </td>
                      <td className="px-4 py-4 align-top text-right">
                        <button
                          type="button"
                          onClick={() => openSubmission(submission)}
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <Eye className="h-4 w-4" />
                          Review
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedSubmission && (
        isLoadingDocument ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="rounded-xl bg-white px-6 py-5 shadow-xl">
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-sigma-600" />
                Loading submission document...
              </div>
            </div>
          </div>
        ) : selectedDocument ? (
          <SubmissionReviewModal
            submission={selectedSubmission}
            document={selectedDocument}
            currentUser={user}
            onClose={() => {
              setSelectedSubmission(null);
              setSelectedDocument(null);
            }}
            onSave={handleSaveGrade}
          />
        ) : (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <div className="flex items-center gap-3 text-red-700">
                <Users className="h-5 w-5" />
                <div>
                  <div className="font-semibold">Document unavailable</div>
                  <div className="text-sm text-gray-600">The linked assessment document could not be loaded.</div>
                </div>
              </div>
              <div className="mt-5 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedSubmission(null);
                    setSelectedDocument(null);
                  }}
                  className="rounded-lg bg-sigma-700 px-4 py-2 text-sm font-semibold text-white hover:bg-sigma-800"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default GradingInboxPage;
